import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { cn, getValue } from '../Shared/lib/utils';
import { Input, Select } from '../Shared/Components/ui';
import { MdOutlineDelete } from 'react-icons/md';
import { FiPlus, FiPlusCircle } from 'react-icons/fi';
import { FaSave, FaTimes } from 'react-icons/fa';

interface Column {
  field: string;
  header: string;
  width?: string;
  selectValue?: string;
  label?: string;
  placeholder?: string;
  type?: string;
  options?: { label: string; value: string | number }[];
  required?: boolean;
  requiredMessage?: string;
  selectOptions?: {
    options:
    | { label: string; value: string | number }[]
    | (() => { label: string; value: string | number }[]);
    placeholder?: string;
    disabled?: boolean;
    getDisplayText?: (value: any, row: Record<string, any>) => string;
    onSelectChange?: (
      value: any,
      row: Record<string, any>,
      rowIndex: number,
      label?: string
    ) => void;
    crudEndpoint?: string;
    entityName?: string;
    onManageClose?: () => void;
    manageColumns?: any[];
    quickAdd?: boolean;
    quickAddField?: string;
  };
  render?: (
    value: any,
    rowIndex: number,
    colIndex: number,
    onChange: (val: any) => void,
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void,
    isEditing: boolean
  ) => React.ReactNode;
  editable?: boolean;
}

interface EditableGridProps {
  required?: boolean;
  allowEdit?: boolean;
  columns: Column[];
  data?: Record<string, any>[];
  className?: string;
  onRowUpdate?: (
    rowId: string | number,
    updates: Record<string, any>
  ) => Promise<void>;
  onRowAdd?: (
    newRow: Record<string, any>
  ) => Promise<Record<string, any> | void>;
  onRowDelete?: (rowId: string | number) => Promise<void>;
  showConfirmModal?: (props: any) => void;
  addButtonLabel?: string;
  defaultRow?: Record<string, any>;
  continuousAdd?: boolean;
  startInAddMode?: boolean;
  emptyStateMessage?: string;
  emptyStateAction?: React.ReactNode;
}

export interface EditableGridHandle {
  addRow: () => void;
}

function EditableGridInner({
  allowEdit = true,
  columns,
  data = [],
  className,
  onRowUpdate,
  onRowAdd,
  onRowDelete,
  showConfirmModal,
  addButtonLabel = 'Add Row',
  defaultRow = {},
  continuousAdd = false,
  startInAddMode = false,
  emptyStateMessage,
  emptyStateAction,
}: EditableGridProps, ref: React.ForwardedRef<EditableGridHandle>) {
  // Generic select renderer for enhanced select columns
  const renderGenericSelect = (
    col: Column,
    row: Record<string, any>,
    rowIndex: number,
    colIndex: number,
    onChange: (val: any) => void,
    onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void,
    isEditing: boolean
  ) => {
    if (!col.selectOptions) return null;

    const { options, placeholder, disabled, getDisplayText, onSelectChange } =
      col.selectOptions;
    const value = getValue(row, col.selectValue || col.field);

    // Handle function-based options
    const resolvedOptions = typeof options === 'function' ? options() : options;

    // Handle readonly mode
    if (!isEditing) {
      const displayText = getDisplayText
        ? getDisplayText(value, row)
        : resolvedOptions?.find(opt => String(opt.value) === String(value))?.label || value || '';
      return <div className="p-2 truncate text-sm">{displayText}</div>;
    }

    // Handle edit mode
    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedValue = e.target.value;
      const selectedLabel = (e.target as any).label || '';
      // Call custom change handler if provided
      if (onSelectChange) {
        onSelectChange(selectedValue, row, rowIndex, selectedLabel);
      }
      // Update the field value
      onChange(selectedValue);
    };

    const {
      crudEndpoint,
      entityName,
      onManageClose,
      manageColumns,
      quickAdd,
      quickAddField,
    } = col.selectOptions;

    return (
      <Select
        label={isSmallScreen ? '' : col.label}
        className="!w-[90%]"
        key={`${rowIndex}-${colIndex}`}
        id={`input-${rowIndex}-${colIndex}`}
        value={value}
        options={resolvedOptions}
        onChange={handleChange}
        onKeyDown={onKeyDown as (e: React.KeyboardEvent) => void}
        placeholder={placeholder}
        disabled={disabled}
        error={fieldErrors[rowIndex]?.[col.field]}
        crudEndpoint={crudEndpoint}
        entityName={entityName}
        onManageClose={onManageClose}
        manageColumns={manageColumns}
        quickAdd={quickAdd}
        quickAddField={quickAddField}
      />
    );
  };
  const [rows, setRows] = useState<Record<string, any>[]>([]);
  const [editingRow, setEditingRow] = useState<number | null>(null);
  const [originalRow, setOriginalRow] = useState<Record<string, any> | null>(
    null
  );
  const [isSmallScreen, setIsSmallScreen] = useState(window.innerWidth <= 900);
  const [recentlyUpdatedRows, setRecentlyUpdatedRows] = useState<Set<number>>(
    new Set()
  );
  const [errorRows, setErrorRows] = useState<Set<number>>(new Set());
  const blurTimeout = useRef<NodeJS.Timeout | null>(null);
  const isCancelling = useRef<boolean>(false);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 900);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (blurTimeout.current) {
        clearTimeout(blurTimeout.current);
      }
    };
  }, []);

  const [fieldErrors, setFieldErrors] = useState<
    Record<number, Record<string, string>>
  >({});

  const [serverErrors, setServerErrors] = useState<Record<number, string>>({});

  const [readinessMessages, setReadinessMessages] = useState<Record<number, string>>({});

  useEffect(() => {
    if (editingRow === null && !hasEmptyRow()) {
      setRows(data || []);
    }
  }, [data]);

  // Handle startInAddMode
  useEffect(() => {
    if (startInAddMode && rows.length > 0 && !hasEmptyRow() && editingRow === null) {
        handleAddRow();
    }
  }, [startInAddMode, rows.length]);

  const containerRef = useRef<HTMLDivElement>(null);

  const parseApiError = (error: any): string => {
    try {
      if (typeof error === 'string') {
        return error;
      }

      if (Array.isArray(error?.errors) && error.errors.length > 0) {
        return String(error.errors[0]);
      }

      if (error?.data) {
        // Handle format: "error": "[ErrorDetail(string='Subject outline already exists...', code='invalid')]"
        const errorStr = String(error.data.error);
        const match = errorStr.match(/string='([^']+)'/);
        if (match) {
          return match[1];
        }
        return errorStr;
      }

      if (error?.details) {
        // Handle nested details
        try {
          const details = JSON.parse(error.details);
          if (details?.error) {
            return parseApiError(details.error);
          }
        } catch {
          // If parsing fails, return as is
        }
        return String(error.details);
      }

      if (error?.message) {
        return error.message;
      }

      return 'An error occurred while saving';
    } catch {
      return 'An error occurred while saving';
    }
  };

  // Check if there's an empty row at the end
  const isValueEmpty = (val: any) => {
    if (val === null || val === undefined) return true;
    if (typeof val === 'string') return val.trim() === '';
    return false;
  };

  const hasEmptyRow = () => {
    if (rows.length === 0) return false;
    const lastRow = rows[rows.length - 1];
    return (
      !lastRow.id &&
      Object.values(lastRow).every(val => isValueEmpty(val))
    );
  };

  const isFormReady = (rowIndex: number): { ready: boolean; reason?: string; details?: any } => {
    const row = rows[rowIndex];
    if (!row) {
      return { ready: false, reason: 'Row not found' };
    }

    // Check if there are any field validation errors for this row
    const rowErrors = fieldErrors[rowIndex];
    if (rowErrors && Object.keys(rowErrors).length > 0) {
      const missingFieldNames = Object.keys(rowErrors)
        .map(fieldName => {
          const column = columns.find(col => col.field === fieldName);
          return column?.header || fieldName;
        });
      return {
        ready: false,
        reason: `Please fill required fields: ${missingFieldNames.join(', ')}`
      };
    }

    // Check if all required fields are filled
    const missingRequiredFields: string[] = [];
    const requiredFieldsStatus: Record<string, any> = {};

    columns.forEach(col => {
      if (col.required) {
        const value = row[col.field];
        const empty = isValueEmpty(value);
        requiredFieldsStatus[col.field] = { value, isEmpty: empty };

        if (empty) {
          missingRequiredFields.push(col.header || col.field);
        }
      }
    });

    if (missingRequiredFields.length > 0) {
      return {
        ready: false,
        reason: `Please fill required fields: ${missingRequiredFields.join(', ')}`,
        details: { missingFields: missingRequiredFields, fieldStatus: requiredFieldsStatus }
      };
    }

    const isEmptyNewRow = !row.id && Object.values(row).every(val => isValueEmpty(val));
    if (isEmptyNewRow) {
      return { ready: false, reason: 'Cannot save empty row' };
    }

    return { ready: true };
  };

  const saveRow = async (rowIndex: number, isExplicitSave: boolean = false): Promise<boolean> => {
    const readinessCheck = isFormReady(rowIndex);
    if (!readinessCheck.ready) {
      if (isExplicitSave) {
        const errors: Record<string, string> = {};
        columns.forEach(col => {
          if (col.required) {
            const value = rows[rowIndex]?.[col.field];
            if (isValueEmpty(value)) {
              errors[col.field] = col.requiredMessage || `${col.field} is required`;
            }
          }
        });

        if (Object.keys(errors).length > 0) {
          setFieldErrors(prev => ({ ...prev, [rowIndex]: errors }));
        }

        setReadinessMessages(prev => ({ ...prev, [rowIndex]: readinessCheck.reason || 'Form not ready' }));

        setTimeout(() => {
          setReadinessMessages(prev => {
            const updated = { ...prev };
            delete updated[rowIndex];
            return updated;
          });
        }, 3000);
      }

      return false;
    }

    setReadinessMessages(prev => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });

    setFieldErrors(prev => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });

    const updatedRow = {
      ...originalRow,
      ...rows[rowIndex],
    };

    const rowId = updatedRow?.id;

    const hasChanged =
      JSON.stringify(updatedRow) !== JSON.stringify(originalRow);

    if (!hasChanged) {
      setEditingRow(null);
      setOriginalRow(null);
      return true;
    }

    const backupRows = [...rows];

    try {
      if (rowId) {
        await onRowUpdate?.(rowId, updatedRow);
        setRecentlyUpdatedRows(prev => new Set(prev).add(rowIndex));
        setTimeout(() => {
          setRecentlyUpdatedRows(prev => {
            const newSet = new Set(prev);
            newSet.delete(rowIndex);
            return newSet;
          });
        }, 2000);
      } else {
        const newRowFromServer = await onRowAdd?.(updatedRow);
        if (newRowFromServer) {
          const newRows = [...rows];
          newRows[rowIndex] = newRowFromServer;
          setRows(newRows);
        }
      }

      setEditingRow(null);
      setOriginalRow(null);

      if (continuousAdd && !rowId) {
        handleAddRow();
      }

      return true;
    } catch (e) {
      console.error('Save failed', e);

      // Parse and store server error message
      const errorMessage = parseApiError(e);
      setServerErrors(prev => ({ ...prev, [rowIndex]: errorMessage }));

      // Keep the red border behavior (existing functionality)
      setRows(backupRows);
      setErrorRows(prev => new Set(prev).add(rowIndex));

      // Clear server error after 5 seconds
      setTimeout(() => {
        setServerErrors(prev => {
          const updated = { ...prev };
          delete updated[rowIndex];
          return updated;
        });
      }, 5000);

      // Clear red border after 3 seconds (existing behavior)
      setTimeout(() => {
        setErrorRows(prev => {
          const newSet = new Set(prev);
          newSet.delete(rowIndex);
          return newSet;
        });
      }, 3000);

      return false;
    }
  };

  const cancelEdit = () => {
    isCancelling.current = true;

    if (editingRow !== null) {
      const currentRow = rows[editingRow];
      const isNewRow = !currentRow?.id;

      if (isNewRow) {
        // Remove the empty row if it's a new row being cancelled
        const newRows = [...rows];
        newRows.splice(editingRow, 1);
        setRows(newRows);
      } else if (originalRow) {
        // Restore original data for existing rows
        const newRows = [...rows];
        newRows[editingRow] = originalRow;
        setRows(newRows);
      }
    }
    setEditingRow(null);
    setOriginalRow(null);

    // Reset the flag after a short delay to allow the blur event to complete
    setTimeout(() => {
      isCancelling.current = false;
    }, 150);
  };

  const handleFieldChange = (rowIndex: number, field: string, value: any) => {
    const newValue = value;
    const oldValue = rows[rowIndex]?.[field];

    setFieldErrors(prev => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });

    setReadinessMessages(prev => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });

    const newRows = [...rows];
    // If the value is an object (row patch), merge it into the row
    if (newValue !== null && typeof newValue === 'object' && !Array.isArray(newValue)) {
      const looksLikeBillBy =
        field === 'billBy' ||
        (Object.prototype.hasOwnProperty.call(newValue, 'type') &&
          Object.prototype.hasOwnProperty.call(newValue, 'value') &&
          Object.prototype.hasOwnProperty.call(newValue, 'label'));

      if (looksLikeBillBy) {
        // Assign structured chooser values to the specific field
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          [field]: newValue,
        };
      } else {
        // Merge patch objects for multi-field updates (used by other grids)
        newRows[rowIndex] = {
          ...newRows[rowIndex],
          ...newValue,
        };
      }
      setRows(newRows);
      return;
    }

    if (newValue !== oldValue) {
      newRows[rowIndex] = {
        ...newRows[rowIndex],
        [field]: newValue,
      };
      setRows(newRows);
    }
  };

  const handleEditKeyDown = async (
    e: React.KeyboardEvent,
    rowIndex: number,
    colIndex: number
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      const dropdownOpen = document.querySelector('.dropdown-open');
      if (dropdownOpen) return;

      const readinessCheck = isFormReady(rowIndex);
      if (readinessCheck.ready) {
        const success = await saveRow(rowIndex, false);
        if (!success) return;
      } else {
        setFieldErrors(prev => {
          const updated = { ...prev };
          delete updated[rowIndex];
          return updated;
        });

        setReadinessMessages(prev => {
          const updated = { ...prev };
          delete updated[rowIndex];
          return updated;
        });
      }

      const isLastRow = rowIndex === rows.length - 1;

      if (isLastRow && !hasEmptyRow()) {
        const newRow: Record<string, any> = {};
        const newRowIndex = rows.length;

        setRows(prev => [...prev, newRow]);
        setEditingRow(newRowIndex);
        setOriginalRow(null);

        setTimeout(() => {
          const input = containerRef.current?.querySelector(
            `#input-${newRowIndex}-0`
          ) as HTMLInputElement;
          input?.focus();
        }, 0);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancelEdit();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const direction = e.shiftKey ? -1 : 1;
      const next = colIndex + direction;
      if (next >= 0 && next < columns.length) {
        const input = containerRef.current?.querySelector(
          `#input-${rowIndex}-${next}`
        ) as HTMLInputElement;
        input?.focus();
      }
    }
  };

  const handleDeleteRow = (rowIndex: number) => {
    const rowId = rows[rowIndex]?.id;

    showConfirmModal?.({
      title: 'Delete Row',
      message: (
        <p>
          Are you sure you want to delete this row? This action cannot be
          undone.
        </p>
      ),
      variant: 'destructive',
      confirmButtonLabel: 'Delete',
      cancelButtonLabel: 'Cancel',
      onConfirm: async () => {
        const backupRows = [...rows];
        try {
          if (rowId) {
            await onRowDelete?.(rowId);
          }
          // Remove the row from local state
          const newRows = [...rows];
          newRows.splice(rowIndex, 1);
          setRows(newRows);
        } catch (e) {
          console.error('Delete failed', e);
          setRows(backupRows);
        }
      },
    });
  };

  const handleAddRow = () => {
    if (editingRow !== null) return;

    if (hasEmptyRow()) {
      const emptyRowIndex = rows.length - 1;
      setEditingRow(emptyRowIndex);
      setOriginalRow({} as Record<string, any>);

      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[emptyRowIndex];
        return updated;
      });

      setReadinessMessages(prev => {
        const updated = { ...prev };
        delete updated[emptyRowIndex];
        return updated;
      });

      setTimeout(() => {
        const input = containerRef.current?.querySelector(
          `#input-${emptyRowIndex}-0`
        ) as HTMLInputElement;
        input?.focus();
      }, 0);
    } else {
      const newRow: Record<string, any> = { ...defaultRow };
      const newRowIndex = rows.length;
      setRows([...rows, newRow]);
      setEditingRow(newRowIndex);
      setOriginalRow(null);

      setFieldErrors(prev => {
        const updated = { ...prev };
        delete updated[newRowIndex];
        return updated;
      });

      setReadinessMessages(prev => {
        const updated = { ...prev };
        delete updated[newRowIndex];
        return updated;
      });

      setTimeout(() => {
        const input = containerRef.current?.querySelector(
          `#input-${newRowIndex}-0`
        ) as HTMLInputElement;
        input?.focus();
      }, 0);
    }
  };

  useImperativeHandle(ref, () => ({ addRow: handleAddRow }));

  const handleEditRow = async (rowIndex: number) => {
    if (!allowEdit) return;

    if (hasEmptyRow() && rowIndex !== rows.length - 1) {
      const emptyRowIndex = rows.length - 1;
      setEditingRow(emptyRowIndex);
      setOriginalRow({} as Record<string, any>);

      setTimeout(() => {
        const input = containerRef.current?.querySelector(
          `#input-${emptyRowIndex}-0`
        ) as HTMLInputElement;
        input?.focus();
      }, 0);
      return;
    }

    if (editingRow !== null && editingRow !== rowIndex) {
      const readinessCheck = isFormReady(editingRow);
      if (readinessCheck.ready) {
        await saveRow(editingRow, false);
      } else {
        setFieldErrors(prev => {
          const updated = { ...prev };
          delete updated[editingRow];
          return updated;
        });

        setReadinessMessages(prev => {
          const updated = { ...prev };
          delete updated[editingRow];
          return updated;
        });
      }
    }

    setEditingRow(rowIndex);
    setOriginalRow({ ...rows[rowIndex] });

    setFieldErrors(prev => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });

    setReadinessMessages(prev => {
      const updated = { ...prev };
      delete updated[rowIndex];
      return updated;
    });

    setTimeout(() => {
      const input = containerRef.current?.querySelector(
        `#input-${rowIndex}-0`
      ) as HTMLInputElement;
      input?.focus();
    }, 0);
  };

  const gridTemplateStyle = {
    gridTemplateColumns: [
      '40px',
      ...columns.map(col => col.width || '1fr'),
      ...(allowEdit ? ['100px'] : []),
    ].join(' '),
  };

  if (rows.length === 0) {
    return (
      <div
        className={cn(
          'w-full bg-white p-3 tablet:p-0 text-sm overflow-x-auto max-h-[600px] overflow-auto',
          className
        )}
        ref={containerRef}
      >
        <div className="flex flex-col items-center justify-center gap-2 py-12 px-4 text-center text-slate-500">
          <FiPlusCircle className="text-2xl text-slate-300" />
          <p className="text-sm">
            {emptyStateMessage ||
              `No items yet — use '${addButtonLabel}' to add one.`}
          </p>
          {allowEdit && (
            <button
              onClick={handleAddRow}
              className="mt-1 inline-flex items-center gap-1 text-[#02676E] hover:underline text-sm font-medium"
            >
              <FiPlus className="text-base" />
              {addButtonLabel}
            </button>
          )}
          {emptyStateAction && <div className="mt-2">{emptyStateAction}</div>}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'w-full bg-white p-3 tablet:p-0 text-sm overflow-x-auto max-h-[600px] overflow-auto',
        className
      )}
      ref={containerRef}
    >
      {/* Header */}
      <div
        className="hidden tablet:grid py-2 px-4 bg-slate-50 border-b border-slate-200 text-slate-500 text-[11px] font-black uppercase tracking-wider items-center sticky top-0 z-10"
        style={gridTemplateStyle}
      >
        <div>#</div>
        {columns.map(col => (
          <div key={col.header} className="truncate">
            {col.header}
          </div>
        ))}
      </div>

      {/* Rows */}
      <div className="divide-y divide-muted tablet:bg-transparent rounded-lg tablet:rounded-none space-y-3 tablet:space-y-0">
        {rows.map((row, rowIndex) => {
          const isEditing = editingRow === rowIndex;

          return (
            <div
              key={rowIndex}
              className={cn(
                errorRows.has(rowIndex) ? 'bg-red-50 border border-red-300' : ''
              )}
            >
              {/* Server Error Display */}
              {serverErrors[rowIndex] && (
                <div className="col-span-full px-4 py-2 bg-red-50 border-l-4 border-red-400 text-red-700 text-sm">
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {serverErrors[rowIndex]}
                  </div>
                </div>
              )}

              {/* Form Readiness Message Display */}
              {readinessMessages[rowIndex] && (
                <div className="col-span-full px-4 py-2 bg-red-50 border-l-4 border-red-400 text-red-700 text-sm">
                  <div className="flex items-center">
                    <svg
                      className="w-4 h-4 mr-2"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {readinessMessages[rowIndex]}
                  </div>
                </div>
              )}
              <div
                role="row"
                className={cn(
                  'relative tablet:grid items-center tablet:px-4 tablet:py-2 hover:bg-slate-50 transition-colors border-b border-slate-100/70 text-slate-700 text-[13px] font-medium',
                  isEditing ? 'tablet:bg-blue-50/50' : ''
                )}
                style={gridTemplateStyle}
                onClick={async () => {
                  if (isEditing || !allowEdit) return;
                  await handleEditRow(rowIndex);
                }}
                onDoubleClick={() => handleEditRow(rowIndex)}
                onFocusCapture={() => {
                  if (blurTimeout.current) {
                    clearTimeout(blurTimeout.current);
                  }
                }}
                onBlurCapture={e => {
                  if (isCancelling.current) {
                    return;
                  }

                  const relatedTarget = e.relatedTarget as HTMLElement;
                  const isWithinRow = e.currentTarget.contains(relatedTarget);
                  const isClickingCancel =
                    relatedTarget?.getAttribute('aria-label') === 'cancel-edit';
                  const isDropdownActive = !!document.querySelector('.MuiPopover-root, .MuiMenu-root');

                  if (!isWithinRow && !isClickingCancel && !isDropdownActive) {
                    blurTimeout.current = setTimeout(() => {
                      if (editingRow === rowIndex) {
                        const readinessCheck = isFormReady(rowIndex);
                        if (readinessCheck.ready) {
                          saveRow(rowIndex, false);
                        } else {
                          const currentRow = rows[rowIndex];
                          const isNewRow = !currentRow?.id;
                          const isEmpty = Object.values(currentRow || {}).every(val => val === undefined || val === '' || val === null);

                          if (isNewRow && isEmpty) {
                            const newRows = [...rows];
                            newRows.splice(rowIndex, 1);
                            setRows(newRows);
                            setEditingRow(null);
                            setOriginalRow(null);
                          } else {
                            setFieldErrors(prev => {
                              const updated = { ...prev };
                              delete updated[rowIndex];
                              return updated;
                            });

                            setReadinessMessages(prev => {
                              const updated = { ...prev };
                              delete updated[rowIndex];
                              return updated;
                            });
                          }

                          return;
                        }
                      }
                    }, 100);
                  }
                }}
              >
                {recentlyUpdatedRows.has(rowIndex) && (
                  <span className="absolute left-5 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-teal-500" />
                )}
                <div className="text-slate-400 font-medium">
                  <span className="tablet:hidden">#</span>
                  {rowIndex + 1}
                </div>
                {columns.map((col, colIndex) => {
                  const val = getValue(row, col?.selectValue || col.field);
                  const resolvedOptions = col.type === 'select'
                    ? (col.options || (col.selectOptions?.options && (typeof col.selectOptions.options === 'function' ? col.selectOptions.options() : col.selectOptions.options)))
                    : null;

                  return (
                    <div
                      key={col.field}
                      className="flex space-y-2.5 tablet:space-y-0 text-sm tablet:text-base tablet:block items-center justify-between relative h-full"
                    >
                      <p className="tablet:hidden capitalize text-gray-500">
                        {col.header}:
                      </p>
                      {isEditing ? (
                        col.render ? (
                          col.render(
                            row,
                            rowIndex,
                            colIndex,
                            newValue => {
                              handleFieldChange(rowIndex, col.field, newValue);
                            },
                            e => handleEditKeyDown(e, rowIndex, colIndex),
                            true
                          )
                        ) : col.editable === false ? (
                          <div
                            className="p-2 truncate text-sm"
                            data-testid={`cell-${rowIndex}-${col.field}`}
                          >
                            {col.selectOptions
                              ? renderGenericSelect(
                                col,
                                row,
                                rowIndex,
                                colIndex,
                                () => { },
                                () => { },
                                false
                              )
                              : col.type === 'select'
                                ? resolvedOptions?.find(
                                  opt => String(opt.value) === String(val)
                                )?.label || ''
                                : val || ''}
                          </div>
                        ) : col.selectOptions ? (
                          renderGenericSelect(
                            col,
                            row,
                            rowIndex,
                            colIndex,
                            newValue => {
                              handleFieldChange(rowIndex, col.field, newValue);
                            },
                            e => handleEditKeyDown(e, rowIndex, colIndex),
                            true
                          )
                        ) : col.type === 'select' ? (
                          <Select
                            label={isSmallScreen ? '' : col.label}
                            className="!w-[63%] tablet:!w-[90%]"
                            key={`${editingRow}-${col.field}`}
                            id={`input-${rowIndex}-${colIndex}`}
                            value={val}
                            options={col.options || []}
                            onChange={e =>
                              handleFieldChange(rowIndex, col.field, e.target.value)
                            }
                            onKeyDown={e =>
                              handleEditKeyDown(e, rowIndex, colIndex)
                            }
                            error={fieldErrors[rowIndex]?.[col.field]}
                          />
                        ) : (
                          <Input
                            id={`input-${rowIndex}-${colIndex}`}
                            label={isSmallScreen ? '' : col.label}
                            className="tablet:!w-[90%]"
                            type={col.type || 'text'}
                            value={String(val)}
                            onChange={e =>
                              handleFieldChange(
                                rowIndex,
                                col.field,
                                e.target.value
                              )
                            }
                            onKeyDown={e =>
                              handleEditKeyDown(e, rowIndex, colIndex)
                            }
                            error={fieldErrors[rowIndex]?.[col.field]}
                          />
                        )
                      ) : (
                        <div
                          className="p-2 truncate text-sm"
                          data-testid={`cell-${rowIndex}-${col.field}`}
                        >
                          {col.render
                            ? col.render(
                              row,
                              rowIndex,
                              colIndex,
                              () => { },
                              () => { },
                              false
                            )
                            : col.selectOptions
                              ? renderGenericSelect(
                                col,
                                row,
                                rowIndex,
                                colIndex,
                                () => { },
                                () => { },
                                false
                              )
                              : col.type === 'select'
                                ? resolvedOptions?.find(
                                  opt => String(opt.value) === String(val)
                                )?.label || ''
                                : val || ''}
                        </div>
                      )}
                    </div>
                  );
                })}
                {allowEdit && (
                  <div className="flex items-center justify-end tablet:justify-start gap-5 pt-3">
                    {isEditing ? (
                      <>
                        <button
                          aria-label="save-row"
                          onClick={() => saveRow(rowIndex, true)}
                        >
                          <FaSave className="text-green-600 w-[17px] h-[17px] tablet:w-4 tablet:h-4 hover:text-green-800 cursor-pointer" />
                        </button>
                        <div
                          aria-label="cancel-edit"
                          onMouseDown={e => {
                            e.preventDefault();
                            cancelEdit();
                          }}
                        >
                          <FaTimes className="text-gray-500 w-[17px] h-[17px] tablet:w-4 tablet:h-4 hover:text-gray-800 cursor-pointer" />
                        </div>
                      </>
                    ) : (
                      <>
                        {Object.values(row).some(val => val !== '') && (
                          <div
                            aria-label="delete-row"
                            onClick={() => handleDeleteRow(rowIndex)}
                          >
                            <MdOutlineDelete className="text-red-500 w-[18px] h-[18px] tablet:w-4 tablet:h-4 hover:text-red-700 cursor-pointer" />
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {allowEdit && (
          <div
            onClick={handleAddRow}
            className={cn(
              'grid items-center px-2 py-2 text-[#02676E] cursor-pointer hover:bg-[#02676E]/10 sticky bottom-0 z-10 bg-white border-t border-slate-200',
              editingRow !== null ? 'pointer-events-none opacity-50' : ''
            )}
            style={gridTemplateStyle}
          >
            <div className="col-span-full text-center flex items-center justify-center gap-2">
              <FiPlusCircle />
              <span>{addButtonLabel}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const EditableGrid = forwardRef(EditableGridInner);
export default EditableGrid;
